import * as React from 'react';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useViewSpecificationPDF } from "../../hooks/useViewSpecificationPDF";
import { SpecificationRecordInterface } from '../../interfaces/Specification'
// custom styled component
import { StyledTableSortLabel } from '../../styles/style';

interface Order {
  target_ym: 'asc' | 'desc';
  request_point_name: 'asc' | 'desc';
  request_point_id: 'asc' | 'desc';
  supply_point_no: 'asc' | 'desc';
  voltage_class: 'asc' | 'desc';
  fee_menu_name_printing: 'asc' | 'desc';
  address: 'asc' | 'desc';
}

type OrderBy = keyof Order;

interface invoiceTable {
  searchedData: SpecificationRecordInterface[];
  page: number;
  pagenation: (page: number) => void;
  allCheck: (state: boolean) => void;
  singleCheck: (target_ym: string, requestPointtId: string) => void;
}

const itemsPerPage: number = Number(process.env.REACT_APP_INVOICE_ITEMS_PER_PAGE) || 20;

const pagenationTheme = createTheme({
  components: {
    MuiPagination: {
      styleOverrides: {
        root: ({theme}) =>
          theme.unstable_sx({
            display: "flex",
            justifyContent: "center"
          }),
        },
      },
    },
  },
);

export default function SpesificationTable(props: invoiceTable) {
  const {searchedData, singleCheck, allCheck, page, pagenation } = props;
  const [order, setOrder] = React.useState<Order>({ 
    target_ym: 'desc',
    request_point_name: 'asc',
    request_point_id: 'asc',
    supply_point_no: 'asc',
    voltage_class: 'asc',
    fee_menu_name_printing: 'asc',
    address: 'asc'
  });
  const [orderBy, setOrderBy] = React.useState<OrderBy>('target_ym');
  const [checkboxBulk, setCheckboxBulk] = React.useState<boolean>(false);
  
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = order[property] === 'asc';
    setOrderBy(property);
    setOrder((prevOrder) => ({
      ...prevOrder,
      [property]: isAsc ? 'desc' : 'asc',
    }));
  };
  
  const {ViewSpecificationPDF} = useViewSpecificationPDF();
  
  const onViewPDFButtonClick = async (targetDate: string, systemFileName: string) => {
    ViewSpecificationPDF(targetDate, systemFileName);
  }
  
  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxBulk(event.target.checked);
    allCheck(event.target.checked);
  };

  interface VoltageMap {
    [key: string]: number;
  }
  
  const voltageMap: VoltageMap = {
    '低圧': 0,
    '高圧': 1,
    '特高': 2
  };

  const compare = (a: SpecificationRecordInterface, b: SpecificationRecordInterface): number => {
    if (orderBy === 'target_ym') {
      if (a.target_ym < b.target_ym) {
        return order.target_ym === 'asc' ? -1 : 1;
      }
      if (a.target_ym > b.target_ym) {
        return order.target_ym === 'asc' ? 1 : -1;
      }
      if (a.target_ym === b.target_ym) {
        if (a.request_point_id < b.request_point_id) {
          return order.request_point_id === 'asc' ? -1 : 1;
        }
        if (a.request_point_id > b.request_point_id) {
          return order.request_point_id === 'asc' ? 1 : -1;
        }
      }
    } 
    if (orderBy === 'request_point_name') {
      if (a.request_point_name < b.request_point_name) {
        return order.request_point_name === 'asc' ? -1 : 1;
      }
      if (a.request_point_name > b.request_point_name) {
        return order.request_point_name === 'asc' ? 1 : -1;
      }
    } else if (orderBy === 'request_point_id') {
      if (a.request_point_id < b.request_point_id) {
        return order.request_point_id === 'asc' ? -1 : 1;
      }
      if (a.request_point_id > b.request_point_id) {
        return order.request_point_id === 'asc' ? 1 : -1;
      }
    } else if (orderBy === 'voltage_class') {
      if (voltageMap[a.voltage_class] < voltageMap[b.voltage_class]) {
        return order.voltage_class === 'asc' ? -1 : 1;
      }
      if (voltageMap[a.voltage_class] > voltageMap[b.voltage_class]) {
        return order.voltage_class === 'asc' ? 1 : -1;
      }
    } else if (orderBy === 'supply_point_no') {
      if (a.supply_point_no < b.supply_point_no) {
        return order.supply_point_no === 'asc' ? -1 : 1;
      }
      if (a.supply_point_no > b.supply_point_no) {
        return order.supply_point_no === 'asc' ? 1 : -1;
      }
    } else if (orderBy === 'fee_menu_name_printing') {
      if (a.fee_menu_name_printing < b.fee_menu_name_printing) {
        return order.fee_menu_name_printing === 'asc' ? -1 : 1;
      }
      if (a.fee_menu_name_printing > b.fee_menu_name_printing) {
        return order.fee_menu_name_printing === 'asc' ? 1 : -1;
      }
    } else if (orderBy === 'address') {
      if (a.address < b.address) {
        return order.address === 'asc' ? -1 : 1;
      }
      if (a.address > b.address) {
        return order.address === 'asc' ? 1 : -1;
      }
    }
    return 0;
  };
    
  return (
    <div>
      <Typography variant="h6">{`${(page-1)*itemsPerPage+1}-${(page)*itemsPerPage < searchedData.length? (page)*itemsPerPage : searchedData.length} / ${searchedData.length}件`}</Typography>
      <TableContainer >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox checked={checkboxBulk} onChange={handleChangeCheckbox} />
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'target_ym'}
                  direction={order.target_ym}
                  onClick={() => handleRequestSort('target_ym')}
                >
                  対象年月
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'request_point_name'}
                  direction={order.request_point_name}
                  onClick={() => handleRequestSort('request_point_name')}
                >
                  需要場所
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'request_point_id'}
                  direction={order.request_point_id}
                  onClick={() => handleRequestSort('request_point_id')}
                >
                  需要場所ID
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'supply_point_no'}
                  direction={order.supply_point_no}
                  onClick={() => handleRequestSort('supply_point_no')}
                >
                  供給地点番号
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'voltage_class'}
                  direction={order.voltage_class}
                  onClick={() => handleRequestSort('voltage_class')}
                >
                  電圧区分
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'fee_menu_name_printing'}
                  direction={order.fee_menu_name_printing}
                  onClick={() => handleRequestSort('fee_menu_name_printing')}
                >
                  契約種別名
                </StyledTableSortLabel>
              </TableCell>
              <TableCell>
                <StyledTableSortLabel
                  active={orderBy === 'address'}
                  direction={order.address}
                  onClick={() => handleRequestSort('address')}
                >
                  住所
                </StyledTableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchedData.sort(compare).slice((page - 1) * itemsPerPage, (page - 1) * itemsPerPage + itemsPerPage).map((row, index) => (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 },
                backgroundColor: index % 2 === 1 ? '#ABE1FA' : '#ffffff' }}
              >
                <TableCell>
                  <Checkbox
                    onChange={() => singleCheck(row.target_ym, row.request_point_id)}
                    checked={row.checked} />
                </TableCell>
                <TableCell>
                  <Button variant="text" onClick={() => onViewPDFButtonClick(row.target_ym, row.system_file_name)}>
                    {row.target_ym}
                  </Button>
                </TableCell>
                <TableCell>{row.request_point_name}</TableCell>
                <TableCell>{row.request_point_id}</TableCell>
                <TableCell>{row.supply_point_no}</TableCell>
                <TableCell>{row.voltage_class}</TableCell>
                <TableCell>{row.fee_menu_name_printing}</TableCell>
                <TableCell>{row.address}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <>
        {
          (searchedData.length === 0) ? <p style={{textAlign: 'center'}}>データがありません</p> : <></>
        }
      </>
      <ThemeProvider theme={pagenationTheme}>
        <Pagination 
          count={(searchedData.length % itemsPerPage === 0) ? (Math.floor((searchedData.length / itemsPerPage) + 1)-1) : (Math.floor((searchedData.length / itemsPerPage) + 1))}
          variant="outlined" 
          shape="rounded" 
          onChange={(e, page) => {
              pagenation(page);
            }
          } 
          page={page}
        />
      </ThemeProvider>
    </div>
  );
}